import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefLensLook = 'brief-lens-pill' | 'brief-lens-dismiss';

const BRIEF_LENS_LOOKS = new Set<string>(['brief-lens-pill', 'brief-lens-dismiss']);
export const isWedgeBriefLensLook = (look: string | undefined): look is WedgeBriefLensLook => look !== undefined && BRIEF_LENS_LOOKS.has(look);

/* Released brief lens controls (the brief top). The lens pill is an inline flex chip with a 6px gap, 5px 10px 5px 9px padding, a full
   round radius, the elevated fill, a half pixel separator border, the secondary label ink, 11.5px bold uppercase type with 0.4px of
   tracking in the Wedge font. The dismiss mark of the lens callout is a 32px box (26px chip inside, drawn by the caller) with 4px of
   padding and -6px margins on three sides, so the visible callout chrome does not shift; it has no fill and no border, a 16px label
   ink glyph and the manipulation touch action. Both keep the declaration order of the source and read the exact legacy roles.
   Neither draws a transparent target or an anchor (wedgeSettingsDrawsHit). The pill is 25px tall in a wrap row whose rows sit 6px
   apart, so two regions of 44px would overlap by 13px; the dismiss mark already owns a 32px box with negative margins, and a region
   would change its tap area. */
export function wedgeBriefLensStyle(look: WedgeBriefLensLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'brief-lens-pill': return {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px 5px 9px', borderRadius: 999,
      background: r('bgElev'), border: `0.5px solid ${r('separator')}`,
      color: r('labelSec'), fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4,
      fontFamily: r('font'), cursor: 'pointer', textTransform: 'uppercase',
    };
    case 'brief-lens-dismiss': return {
      background: 'none', border: 0, padding: 4,
      minWidth: 32, minHeight: 32, marginRight: -6, marginTop: -6, marginBottom: -6,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, color: r('label'), cursor: 'pointer',
      fontFamily: r('font'), lineHeight: 1,
      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
    };
  }
}
